import { z } from "zod";
import { paletteForColours, type Palette } from "./palette.js";
import { defaultCardMotion, type CardElement } from "./card.js";
import { bookendFonts } from "./font.js";
import { assetKeySchema, colorSchema } from "./primitives.js";
import type { Background } from "./scene.js";

/**
 * One set of choices every new video starts from.
 *
 * A workspace makes the same video over and over — the same product, the same
 * palette, the same voice — and setting all of it per project is how the fifth
 * one ends up looking like a different company made it. This is the answer
 * given once.
 *
 * Deliberately small. It holds what a card is actually built from and what the
 * narration is written in; anything else here would be a second place to change
 * a setting that already has a home.
 */
export const brandColorsSchema = z.object({
  /** Words on a card. */
  text: colorSchema.default("#f8fafc"),
  /** What they sit on. */
  background: colorSchema.default("#0b1020"),
  accent: colorSchema.default("#ff6900"),
  accentAlt: colorSchema.default("#38bdf8"),
});

export const brandKitSchema = z.object({
  /**
   * When somebody last saved the kit.
   *
   * The colours alone cannot answer "has a brand been set", because every one
   * of them has a default and choosing a colour that happens to match one is a
   * real choice. Written on save; absent on kits saved before it existed, where
   * the colour comparison is the fallback.
   */
  configuredAt: z.string().max(40).optional(),
  /**
   * Where the kit came from, when someone said. Stored rather than used: there
   * is no fetch behind it yet, and a field that quietly does nothing is better
   * named than invented later.
   */
  website: z.string().max(200).default(""),
  logoKey: assetKeySchema.optional(),
  /**
   * Whether the mark appears on title cards.
   *
   * A real choice rather than a default nobody can change. A wordmark already
   * reading as the product name makes a second copy of it redundant, and some
   * brands want the card to be the words alone — but most want their logo on
   * it, so this starts on and only matters once a logo exists.
   */
  showLogo: z.boolean().default(true),
  /**
   * Who reads the narration.
   *
   * On the kit rather than in provider settings, because a narrator is part of
   * how a company sounds — the same answer as the tone, given once. The
   * deployment-wide `elevenLabsVoiceId` stays as the fallback, which is what
   * a self-hosted install with one kit has always used.
   *
   * An opaque id from ElevenLabs, so it is bounded and never parsed.
   */
  voiceId: z.string().max(64).optional(),
  colors: brandColorsSchema.default({}),
  /** Family names from the bundled library — see `brandFontStack`. */
  titleFont: z.string().max(80).default("Inter"),
  bodyFont: z.string().max(80).default("Inter"),
  /**
   * How the narration should sound. Feeds the script prompt as the default for
   * a project that has not said something more specific.
   */
  tone: z.string().max(400).default(""),
});

export type BrandColors = z.infer<typeof brandColorsSchema>;
export type BrandKit = z.infer<typeof brandKitSchema>;

export const defaultBrandKit: BrandKit = brandKitSchema.parse({});

/**
 * The CSS stack for a family the kit names.
 *
 * A kit can carry a family this release does not have — read from a website,
 * or left over from an older list. Passing it through would render as whatever
 * the machine happened to have and look like the brand had simply been ignored,
 * so an unknown name falls back to a face that is definitely here.
 */
export function brandFontStack(family: string): string {
  const found = bookendFonts.find((font) => font.family === family);
  return (found ?? bookendFonts[0]).stack;
}

/** The kit's background, in the shape the renderer already takes. */
export function brandBackground(kit: BrandKit): Background {
  return { kind: "color", color: kit.colors.background };
}

/** Marks the palette built from the kit rather than taken from the library. */
export const BRAND_PALETTE_ID = -1;

/**
 * The workspace's own colours, as a palette.
 *
 * For the picker: something to show, select and compare against. Backgrounds are
 * built by `brandBackgroundFor` rather than from this, because a palette has to
 * infer which colour is the ground and a kit has already been told.
 */
export function brandPalette(kit: BrandKit): Palette {
  const colours = [kit.colors.background, kit.colors.accent, kit.colors.accentAlt, kit.colors.text];
  return { id: BRAND_PALETTE_ID, family: paletteForColours(colours).family, colours };
}

/**
 * A backdrop in the workspace's colours, in any of the four styles.
 *
 * Not routed through `paletteBackground`, and that is the point. `paletteRoles`
 * decides which colour is the ground by sorting on luminance — a reasonable
 * guess for an anonymous set of four, and the wrong thing entirely here, where
 * `colors.background` is literally the field named "what they sit on". With the
 * stock kit it chose the near-white text as the ground and put the brand's own
 * background in the fields.
 *
 * So the roles are read rather than inferred: the background is the ground, the
 * two accents are what moves over it, and the text colour stays out of it — it
 * is for words, and a backdrop wearing the colour its captions are written in is
 * a backdrop those captions cannot be read on.
 */
export function brandBackgroundFor(kind: Background["kind"], kit: BrandKit): Background {
  const { background, accent, accentAlt } = kit.colors;
  if (kind === "mesh") return { kind: "mesh", layout: "drift", ground: background, fields: [accent, accentAlt] };
  if (kind === "gradient") return { kind: "gradient", from: background, to: accent, angle: 160 };
  if (kind === "pattern") return { kind: "pattern", pattern: "linear", from: background, to: accent };
  return { kind: "color", color: background };
}

export function isBrandConfigured(kit: BrandKit): boolean {
  if (kit.configuredAt) return true;

  const colours = brandColorsSchema.parse({});
  const recoloured = (Object.keys(colours) as (keyof typeof colours)[]).some(
    (role) => kit.colors[role]?.toLowerCase() !== colours[role].toLowerCase(),
  );

  // Faces count as much as colours. Checking only the palette called a kit with
  // Space Grotesk on both faces "unconfigured" — somebody had plainly set their
  // brand, and every control that asks this question was hidden from them.
  const blank = brandKitSchema.parse({});
  const refaced = kit.titleFont !== blank.titleFont || kit.bodyFont !== blank.bodyFont;

  return recoloured || refaced;
}

/**
 * Whether words in one colour can be read on another.
 *
 * Someone will paste two hexes out of a palette and not look, and a title that
 * cannot be read is a worse failure than a title in the wrong font — it looks
 * like the render broke. The threshold is WCAG's 4.5:1, which is the line
 * between "small text is hard work" and "small text is fine".
 *
 * A colour this cannot parse — an `rgba()`, a named colour — is reported as
 * readable rather than guessed at. Refusing to answer is not the same as
 * answering no, and a warning nobody can act on is noise.
 */
export function brandReadability(text: string, background: string): { readable: boolean; ratio: number } {
  const a = relativeLuminance(text);
  const b = relativeLuminance(background);
  if (a === null || b === null) return { readable: true, ratio: 0 };

  const ratio = (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  return { readable: ratio >= 4.5, ratio: Number(ratio.toFixed(2)) };
}

/**
 * Dresses a card in the workspace's colours and faces.
 *
 * Only the words: a shape's fill is a decision someone made about that shape,
 * and a picture has no typeface. Position and motion are left exactly as they
 * are, because whatever laid the card out — a preset, or the migration from the
 * old title and subtitle — knew more about it than a palette does.
 *
 * The title takes the title face and the rest take the body face, which is what
 * the two fields on the panel mean. Nothing here writes text.
 */
/**
 * The id a brand logo element carries.
 *
 * Fixed rather than generated, because this runs every time a card is rebuilt
 * and the element has to be recognisable as the one it placed last time —
 * otherwise a rebuild stacks a second logo on the first, and a third on that.
 */
export const brandLogoElementId = "brand-logo";

export function applyBrandToCard<T extends { kind: "card"; elements: CardElement[] }>(card: T, kit: BrandKit): T {
  const elements = card.elements.map((element) =>
    element.kind === "text"
      ? {
          ...element,
          style: {
            ...element.style,
            fontFamily: brandFontStack(element.role === "title" ? kit.titleFont : kit.bodyFont),
            color: kit.colors.text,
          },
        }
      : element,
  );

  /**
   * The mark itself, when the workspace has one and wants it shown.
   *
   * Kept in step rather than added once: a kit whose logo changed should
   * re-dress a card it already dressed, and one where the switch was turned
   * off should lose it. Anything the person placed themselves is untouched —
   * only the element this function owns is replaced.
   *
   * Above the type, which is where a title card puts a logo, and sized as a
   * fraction of the frame like everything else on a card.
   */
  const withoutOurs = elements.filter((element) => element.id !== brandLogoElementId);
  if (!kit.showLogo || !kit.logoKey) return { ...card, elements: withoutOurs };

  const logo: CardElement = {
    id: brandLogoElementId,
    kind: "image",
    anchor: "top-center",
    x: 0.3,
    y: 0.12,
    width: 0.4,
    height: 0.16,
    assetKey: kit.logoKey,
    fit: "contain",
    radius: 0,
    opacity: 1,
    motion: { ...defaultCardMotion },
  };

  // First, so it sits behind nothing and reads as the top of the card.
  return { ...card, elements: [logo, ...withoutOurs] };
}

function relativeLuminance(colour: string): number | null {
  const hex = /^#([0-9a-f]{6})$/i.exec(colour.trim());
  if (!hex) return null;

  const value = parseInt(hex[1], 16);
  const channels = [(value >> 16) & 255, (value >> 8) & 255, value & 255]
    .map((channel) => channel / 255)
    .map((channel) => (channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4));

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}
